import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import i18n from "../../i18n";
import {
  normalizeAppLocale,
  type AppLocale,
} from "../constants/app-locales";

interface LanguageContextType {
  language: AppLocale;
  isRTL: boolean;
  setLanguage: (lang: AppLocale) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  // * Align with i18next-browser-languagedetector (cookie + localStorage in i18n.ts).
  // ! Do not call changeLanguage("en") here — that overwrote the saved locale on every load.
  const [language, setLanguageState] = useState<AppLocale>(() =>
    normalizeAppLocale(i18n.language),
  );
  const isRTL = language === "ar";

  const setLanguage = (lang: AppLocale) => {
    setLanguageState(lang);
    void i18n.changeLanguage(lang);
  };

  const updateDocumentDirection = (lang: AppLocale) => {
    const htmlElement = document.documentElement;
    const isRtl = lang === "ar";

    htmlElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
    htmlElement.setAttribute("lang", lang);

    htmlElement.classList.toggle("rtl", isRtl);
  };

  useEffect(() => {
    updateDocumentDirection(language);
  }, [language]);

  // * Keep context in sync if i18n language changes outside this provider
  useEffect(() => {
    const onLng = (lng: string) => {
      setLanguageState(normalizeAppLocale(lng));
    };
    i18n.on("languageChanged", onLng);
    return () => {
      i18n.off("languageChanged", onLng);
    };
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        isRTL,
        setLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
